import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Header,
  SpaceBetween,
  Box,
  Button,
  ButtonDropdown,
  Table,
  TextFilter,
  Container,
  Badge,
  Select,
  FormField,
  StatusIndicator,
  Multiselect,
  Modal,
  Input,
  ExpandableSection,
  Pagination,
  Flashbar,
  FlashbarProps,
  Alert,
  Autosuggest,
  Icon,
} from "@cloudscape-design/components";
import Tooltip from "@cloudscape-design/components/tooltip";
import { OclViewItem, DataViewMode, EntityType } from "./types";
import {
  PAGE_SIZE,
  ENTITY_TYPE_OPTIONS,
  ENTITY_TYPE_LABEL,
  SYSTEM,
  ALL_OPTION,
} from "./constants";
import { getRoleBadgeColor, getRoleAbbreviation, handleMultiselectAll, getFeatureLabel, buildActionIdToNameMap, formatDateTime, isValidEmail, isValidVendorEmail, transformAssignmentsToOclView } from "./helpers";
import { getPermissionAssignmentsRequest, getPermissionMeRequest, upsertPermissionAssignmentRequest, getPermissionScopeProgramsRequest, getPermissionRolesRequest, changePermissionAssignmentStatusRequest } from "../../redux/actions/permissionActions";
import { searchEmployeeRequest, searchTeamRequest } from "../../redux/actions/apiActions";
import { RootState } from "../../redux/reducers/rootReducer";
import { PermissionAssignment } from "../../redux/types/permissionTypes";
import { usePermission, DataRecord } from "../../hooks/usePermission";

const RoleBadgeWithTooltip: React.FC<{ role: string; color: any }> = ({ role, color }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  return (
    <span
      ref={ref}
      onPointerEnter={() => setVisible(true)}
      onPointerLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <Badge color={color}>{getRoleAbbreviation(role)}</Badge>
      {visible && (
        <Tooltip content={role} getTrack={() => ref.current} onEscape={() => setVisible(false)} position="top" />
      )}
    </span>
  );
};

export interface UsersAndRolesTabProps {
  selectedSystem: string;
}

const UsersAndRolesTab: React.FC<UsersAndRolesTabProps> = ({ selectedSystem }) => {
  const dispatch = useDispatch();
  const { hasPermission, hasDataPermission } = usePermission();
  const meData = useSelector((state: RootState) => state.permission.me?.data);
  const constantsData = useSelector((state: RootState) => state.permission.constants?.data);
  const scopeOptionsData = useSelector((state: RootState) => state.permission.scopeOptions?.data);
  const scopeProgramsData = useSelector((state: RootState) => state.permission.scopePrograms?.data);
  const rolesData = useSelector((state: RootState) => state.permission.roles.data);
  const rolesStatus = useSelector((state: RootState) => state.permission.roles?.status);
  const { data: assignmentsData, status: assignmentsStatus } = useSelector(
    (state: RootState) => state.permission.assignments
  );
  const upsertAssignmentStatus = useSelector((state: RootState) => state.permission.upsertAssignment?.status);
  const upsertAssignmentError = useSelector((state: RootState) => state.permission.upsertAssignment?.error);
  const changeAssignmentStatusState = useSelector((state: RootState) => state.permission.changeAssignmentStatus);

  // System options from /permission/constants API -> systems
  const systemOptions = useMemo(() => {
    if (constantsData?.systems?.length) {
      return constantsData.systems.map((s) => ({ label: s, value: s }));
    }
    return [];
  }, [constantsData]);

  // Operation options from /permission/constants API -> factory_operations, disabled if no permission
  const operationOptions = useMemo(() => {
    if (constantsData?.factory_operations?.length) {
      const ops = constantsData.factory_operations.map((op) => ({
        label: op,
        value: op,
        disabled: !hasDataPermission("permission_management", "pm_view_users_roles", { operation: op }),
      }));
      const hasAnyAllowed = ops.some((o) => !o.disabled);
      return [
        { ...ALL_OPTION, description: "All operations", disabled: !hasAnyAllowed },
        ...ops,
      ];
    }
    return [];
  }, [constantsData, hasDataPermission]);

  // CM/Location grouped options from /permission/scope-options API, disabled if no permission
  const cmLocationOptions = useMemo(() => {
    if (!scopeOptionsData?.cm_locations_options?.length) return [];

    const cmMap = new Map<string, { label: string; value: string; disabled: boolean }[]>();
    scopeOptionsData.cm_locations_options.forEach((item) => {
      if (!cmMap.has(item.cm)) {
        cmMap.set(item.cm, []);
      }
      cmMap.get(item.cm)!.push({
        label: item.cm_location,
        value: `${item.cm}|${item.cm_location}`,
        disabled: !hasDataPermission("permission_management", "pm_view_users_roles", { cm: item.cm, location: item.cm_location }),
      });
    });

    const groups: any[] = [];
    let hasAnyAllowed = false;

    cmMap.forEach((locations, cm) => {
      if (locations.some((loc) => !loc.disabled)) hasAnyAllowed = true;
      groups.push({ label: cm, options: locations });
    });

    return [
      { ...ALL_OPTION, description: "All CMs and locations", disabled: !hasAnyAllowed },
      ...groups,
    ];
  }, [scopeOptionsData, hasDataPermission]);

  // Program options from /permission/scope-programs API, disabled if no permission
  const programOptions = useMemo(() => {
    if (scopeProgramsData?.programs?.length) {
      const progs = scopeProgramsData.programs.map((p) => ({
        label: p,
        value: p,
        disabled: !hasDataPermission("permission_management", "pm_view_users_roles", { program: p }),
      }));
      const hasAnyAllowed = progs.some((p) => !p.disabled);
      return [
        { ...ALL_OPTION, description: "All programs", disabled: !hasAnyAllowed },
        ...progs,
      ];
    }
    return [];
  }, [scopeProgramsData, hasDataPermission]);

  // Current user's all roles (deduplicated) from /permission/me API -> assignments
  const currentUserRoles = useMemo(() => {
    if (!meData?.assignments) return [];
    const roleSet = new Set<string>();
    meData.assignments.forEach((a) => {
      a.roles.forEach((r) => roleSet.add(r));
    });
    return Array.from(roleSet);
  }, [meData]);

  // Determine if a row should be disabled based on current user's role level
  const isAssignmentDisabled = useCallback((
    itemRoles: string[],
    createdBy: string,
  ) => {
    if (!meData) return false;
    const system = selectedSystem === "All" ? SYSTEM : selectedSystem;

    // System Admin: never disabled
    if (meData.is_system_admin?.[system]) return false;

    // Determine current user's role level for this system
    const myRoles = new Set<string>();
    meData.assignments
      ?.filter((a) => a.system === system)
      .forEach((a) => a.roles.forEach((r) => myRoles.add(r)));

    const isAdmin = myRoles.has("Administrator");

    if (isAdmin) {
      if (itemRoles.includes("System Admin")) return true;
      if (itemRoles.includes("Administrator") && createdBy !== meData.user) return true;
      return false;
    }

    // Custom (or other low-level roles): disabled if row has admin roles, otherwise only self-created can be modified
    if (itemRoles.includes("System Admin") || itemRoles.includes("Administrator")) return true;
    if (createdBy !== meData.user) return true;

    return false;
  }, [meData, selectedSystem]);

  // Determine if a row should be disabled based on datascope coverage
  const isDataScopeDisabled = useCallback((record?: DataRecord) => {
    if (!record) return false;
    return !hasDataPermission("permission_management", "pm_assign_role", record);
  }, [hasDataPermission]);

  // ─── Local State ─────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<DataViewMode>("by-user");
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [oclCurrentPage, setOclCurrentPage] = useState(1);
  const [sortingColumn, setSortingColumn] = useState<{ sortingField?: string }>({});
  const [sortingDescending, setSortingDescending] = useState(false);
  const [selectedItems, setSelectedItems] = useState<PermissionAssignment[]>([]);
  const [selectedOclItems, setSelectedOclItems] = useState<OclViewItem[]>([]);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Shared modal state
  const [modalSystem, setModalSystem] = useState<any>(
    selectedSystem === "All" ? null : { label: selectedSystem, value: selectedSystem }
  );
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [statusAction, setStatusAction] = useState<"activate" | "deactivate">("activate");

  // By-user modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState<number>(1);
  const [modalUserType, setModalUserType] = useState<any>(ENTITY_TYPE_OPTIONS[0]);
  const [modalUser, setModalUser] = useState("");
  const [modalTeamDisplay, setModalTeamDisplay] = useState("");
  const [modalRoles, setModalRoles] = useState<readonly any[]>([]);
  const [modalOperations, setModalOperations] = useState<readonly any[]>([]);
  const [modalCmLocations, setModalCmLocations] = useState<readonly any[]>([]);
  const [modalPrograms, setModalPrograms] = useState<readonly any[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);

  // Employee search (autosuggest for amazon-user)
  const [employeeSuggestions, setEmployeeSuggestions] = useState<{ label: string; value: string }[]>([]);
  const employeeSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { employeeSearchResults, employeeSearchLoading, teamSearchResults, teamSearchLoading } = useSelector((state: RootState) => state.api);

  useEffect(() => {
    if (employeeSearchResults) {
      const options = Array.isArray(employeeSearchResults)
        ? employeeSearchResults.map((item: any) => {
            if (typeof item === "string") return { label: item, value: item };
            const email = item.email || item.login_email || (item.login ? `${item.login}@amazon.com` : "");
            const label = item.name ? `${item.name} (${email})` : email;
            return { label, value: email };
          })
        : [];
      setEmployeeSuggestions(options);
    }
  }, [employeeSearchResults]);

  const fetchEmployeeSuggestions = useCallback((searchText: string) => {
    if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    const trimmed = searchText.trim();
    if (trimmed.length < 2 || isValidEmail(trimmed)) {
      setEmployeeSuggestions([]);
      return;
    }
    employeeSearchTimerRef.current = setTimeout(() => {
      const searchKey = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
      dispatch(searchEmployeeRequest(searchKey));
    }, 300);
  }, [dispatch]);

  useEffect(() => {
    return () => { if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current); };
  }, []);

  // Team search (autosuggest for team type)
  const [teamSuggestions, setTeamSuggestions] = useState<{ label: string; value: string }[]>([]);
  const teamSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (teamSearchResults) {
      const options = Array.isArray(teamSearchResults)
        ? teamSearchResults.map((item: any) => {
            if (typeof item === "string") return { label: item, value: item };
            return { label: item.teamName || "", value: item.teamId || "" };
          })
        : [];
      setTeamSuggestions(options);
    }
  }, [teamSearchResults]);

  const fetchTeamSuggestions = useCallback((searchText: string) => {
    if (teamSearchTimerRef.current) clearTimeout(teamSearchTimerRef.current);
    const trimmed = searchText.trim();
    if (trimmed.length < 2) {
      setTeamSuggestions([]);
      return;
    }
    teamSearchTimerRef.current = setTimeout(() => {
      dispatch(searchTeamRequest(trimmed));
    }, 300);
  }, [dispatch]);

  useEffect(() => {
    return () => { if (teamSearchTimerRef.current) clearTimeout(teamSearchTimerRef.current); };
  }, []);

  // By-ocl modal state (dataScope selections reuse modalOperations/modalCmLocations/modalPrograms since both modals are mutually exclusive)
  const [oclModalVisible, setOclModalVisible] = useState(false);
  const [oclEditingMode, setOclEditingMode] = useState(false);
  const [oclTableRows, setOclTableRows] = useState<{ assignmentId?: string; user: string; entityType: EntityType; roles: string[]; version?: number; teamDisplay?: string }[]>([]);
  const [oclRemovedRows, setOclRemovedRows] = useState<{ assignmentId: string; user: string; entityType: EntityType }[]>([]);
  const [oclModalError, setOclModalError] = useState<string | null>(null);
  const oclOriginalProgramsRef = useRef<string[]>([]);
  const oclOriginalRowsRef = useRef<{ assignmentId?: string; user: string; roles: string[] }[]>([]);

  // ─── Derived Data ────────────────────────────────────────────────────────
  const assignableRoleOptions = useMemo(() => {
    if (!meData?.assignable_roles?.length) return [];
    return meData.assignable_roles.map((roleName) => {
      const roleInfo = rolesData?.find((r) => r.role_name === roleName);
      return {
        label: roleName,
        value: roleName,
        description: roleInfo?.description || "",
      };
    });
  }, [meData, rolesData]);

  // Roles already assigned to the modal user in OTHER assignments (to disable in dropdown)
  const userExistingRoles = useMemo(() => {
    if (!modalUser.trim() || !assignmentsData?.assignments) return new Set<string>();
    const roles = new Set<string>();
    assignmentsData.assignments.forEach((a) => {
      if (a.entity === modalUser.trim() && a.assignment_id !== editingId) {
        a.roles.forEach((r) => roles.add(r));
      }
    });
    return roles;
  }, [modalUser, assignmentsData, editingId]);

  // Check if the current user+roles combination already exists in the system
  const isDuplicateAssignment = useMemo(() => {
    if (!modalUser.trim() || modalRoles.length === 0 || !assignmentsData?.assignments) return false;
    const system = selectedSystem === "All" ? modalSystem?.value : selectedSystem;
    const selectedRoleNames = new Set(modalRoles.map((r: any) => r.label));
    return assignmentsData.assignments.some((a) => {
      if (editingId && a.assignment_id === editingId) return false;
      if (system && a.system !== system) return false;
      if (a.entity !== modalUser.trim()) return false;
      return [...selectedRoleNames].every((r) => a.roles.includes(r));
    });
  }, [modalUser, modalRoles, assignmentsData, editingId, selectedSystem, modalSystem]);

  const modalRolePreviews = useMemo(() => {
    if (!rolesData) return [];
    return modalRoles
      .map((r) => rolesData.find((role) => role.role_name === r.value))
      .filter(Boolean) as typeof rolesData;
  }, [modalRoles, rolesData]);

  const actionIdToName = useMemo(() => buildActionIdToNameMap(constantsData?.feature_actions), [constantsData]);

  // Existing assignment suggestions for OCL modal user input
  const existingAssignmentSuggestions = useMemo(() => {
    if (!assignmentsData?.assignments) return [];
    const system = selectedSystem === "All" ? modalSystem?.value : selectedSystem;
    const existingIds = new Set(oclTableRows.filter((r) => r.assignmentId).map((r) => r.assignmentId));
    return assignmentsData.assignments
      .filter((a) => {
        if (system && a.system !== system) return false;
        if (existingIds.has(a.assignment_id)) return false;
        if (isAssignmentDisabled(a.roles, a.created_by)) return false;
        return true;
      })
      .map((a) => ({
        label: a.entity,
        value: a.assignment_id,
        description: `Roles: ${a.roles.join(", ")}`,
      }));
  }, [assignmentsData, selectedSystem, modalSystem, oclTableRows, isAssignmentDisabled]);

  // OCL users table: all rows are stored in oclTableRows; the "Add" button is outside the table
  const oclUsersTableItems = useMemo(() => {
    return oclTableRows.map((row, idx) => ({ ...row, idx }));
  }, [oclTableRows]);

  // Check if a row's user+roles already exist in system assignments (for warning display)
  const isRowExistingInSystem = useCallback((row: { assignmentId?: string; user: string; entityType: EntityType; roles: string[] }) => {
    if (row.assignmentId) return true;
    if (!row.user.trim() || !assignmentsData?.assignments) return false;
    return assignmentsData.assignments.some(
      (a) => a.entity === row.user.trim() && a.entity_type === row.entityType && row.roles.every((r) => a.roles.includes(r))
    );
  }, [assignmentsData]);

  // OCL modal: check if there are any data changes (added/removed rows or programs changed)
  const oclHasChanges = useMemo(() => {
    const validRows = oclTableRows.filter((row) => row.user.trim() && row.roles.length > 0);
    const originalRows = oclOriginalRowsRef.current;

    // Compare current valid rows with original: check if same set of users/roles
    const currentKey = validRows
      .map((r) => `${r.assignmentId || ""}|${r.user}|${r.roles.sort().join(",")}`)
      .sort()
      .join(";");
    const originalKey = originalRows
      .map((r) => `${r.assignmentId || ""}|${r.user}|${r.roles.sort().join(",")}`)
      .sort()
      .join(";");
    const rowsChanged = currentKey !== originalKey;

    const currentPrograms = modalPrograms.some((p: any) => p.value === "All")
      ? ["All"]
      : modalPrograms.map((p: any) => p.value).sort();
    const originalPrograms = [...oclOriginalProgramsRef.current].sort();
    const programsChanged = currentPrograms.length !== originalPrograms.length ||
      currentPrograms.some((p, i) => p !== originalPrograms[i]);
    return rowsChanged || programsChanged;
  }, [oclTableRows, oclRemovedRows, modalPrograms]);

  const filteredData: PermissionAssignment[] = useMemo(() => {
    const all = assignmentsData?.assignments || [];
    if (!filterText) return all;
    const lower = filterText.toLowerCase();
    return all.filter((item) =>
      item.entity.toLowerCase().includes(lower) ||
      (item.entity_display_name && item.entity_display_name.toLowerCase().includes(lower)) ||
      item.roles.some((r) => r.toLowerCase().includes(lower)) ||
      item.operations.some((o) => o.toLowerCase().includes(lower)) ||
      item.cm_locations.some((cl) => cl.cm.toLowerCase().includes(lower))
    );
  }, [assignmentsData, filterText]);

  const sortedData = useMemo(() => {
    if (!sortingColumn.sortingField) return filteredData;
    const field = sortingColumn.sortingField as keyof PermissionAssignment;
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortingDescending ? sorted.reverse() : sorted;
  }, [filteredData, sortingColumn, sortingDescending]);

  const totalCount = sortedData.length;

  const transformedOclView = useMemo(() => transformAssignmentsToOclView(assignmentsData), [assignmentsData]);

  const filteredByOcl = (selectedSystem === "All" ? transformedOclView : transformedOclView.filter((item) => item.system === selectedSystem))
    .filter((item) => {
      if (!filterText) return true;
      const lower = filterText.toLowerCase();
      return (
        item.operation.toLowerCase().includes(lower) ||
        item.cm.toLowerCase().includes(lower) ||
        item.location.toLowerCase().includes(lower) ||
        item.users.some((u) => u.user.toLowerCase().includes(lower))
      );
    });

  const hasSelection = viewMode === "by-user" ? selectedItems.length > 0 : selectedOclItems.length > 0;
  const hasSingleSelection = viewMode === "by-user" ? selectedItems.length === 1 : selectedOclItems.length === 1;

  // ─── Effects ─────────────────────────────────────────────────────────────
  // Programs loading state + pending selection for edit mode (avoids race condition)
  const scopeProgramsStatus = useSelector((state: RootState) => state.permission.scopePrograms?.status);
  const programsLoading = scopeProgramsStatus === "loading";
  const pendingModalProgramsRef = useRef<readonly any[] | null>(null);

  // Fetch roles and programs on mount
  useEffect(() => {
    if (!rolesStatus || rolesStatus === "idle") {
      dispatch(getPermissionRolesRequest(SYSTEM));
    }
    // if (!scopeProgramsStatus || scopeProgramsStatus === "idle") {
    //   dispatch(getPermissionScopeProgramsRequest({ cm_location: [{ cm: "All", cm_location: ["ALL"] }] }));
    // }
  }, [dispatch, rolesStatus, scopeProgramsStatus]);

  // Fetch assignments list when system changes
  useEffect(() => {
    dispatch(getPermissionAssignmentsRequest({
      system: selectedSystem,
    }));
  }, [dispatch, selectedSystem]);

  const hasDisabledOperation = useMemo(() => {
    return operationOptions.some((opt: any) => opt.value !== "All" && opt.disabled);
  }, [operationOptions]);

  const hasDisabledCmLocation = useMemo(() => {
    return cmLocationOptions.some((opt: any) => {
      if (opt.options) return opt.options.some((child: any) => child.disabled);
      return opt.value !== "All" && opt.disabled;
    });
  }, [cmLocationOptions]);

  const hasDisabledProgram = useMemo(() => {
    return programOptions.some((opt: any) => opt.value !== "All" && opt.disabled);
  }, [programOptions]);

  const fetchScopePrograms = useCallback((cmLocs: readonly any[]) => {
    if (cmLocs.length === 0) return;
    if (cmLocs.some((o: any) => o.value === "All") && !hasDisabledCmLocation) {
      dispatch(getPermissionScopeProgramsRequest({ cm_location: [{ cm: "All", cm_location: ["ALL"] }] }));
      return;
    }
    // When there are disabled items, use actual leaf selections to build payload
    const leafLocs = cmLocs.filter((o: any) => o.value !== "All");
    if (leafLocs.length === 0) return;
    const cmLocMap = new Map<string, string[]>();
    leafLocs.forEach((opt: any) => {
      const [cm, loc] = (opt.value as string).split("|");
      if (!cmLocMap.has(cm)) cmLocMap.set(cm, []);
      cmLocMap.get(cm)!.push(loc);
    });
    const payload: { cm: string; cm_location: string[] }[] = [];
    cmLocMap.forEach((locs, cm) => payload.push({ cm, cm_location: locs.includes("All") ? ["ALL"] : locs }));
    if (payload.length > 0) dispatch(getPermissionScopeProgramsRequest({ cm_location: payload }));
  }, [dispatch, hasDisabledCmLocation]);

  // Re-expand "All" selections after options load (handles first-open timing for both modals)
  const isEditingModal = (modalVisible && editingId) || (oclModalVisible && oclEditingMode);
  useEffect(() => {
    if (operationOptions.length === 0 || !isEditingModal) return;
    if (modalOperations.some((o: any) => o.value === "All") && modalOperations.length <= 1) {
      const allOps: any[] = [ALL_OPTION];
      operationOptions.forEach((opt) => { if (opt.value !== "All" && !opt.disabled) allOps.push(opt); });
      setModalOperations(allOps);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationOptions]);

  useEffect(() => {
    if (cmLocationOptions.length === 0 || !isEditingModal) return;
    if (modalCmLocations.some((o: any) => o.value === "All") && modalCmLocations.length <= 1) {
      const allCmLocs: any[] = [ALL_OPTION];
      cmLocationOptions.forEach((opt) => {
        if (opt.options) { opt.options.forEach((child: any) => { if (!child.disabled) allCmLocs.push(child); }); }
        else if (opt.value !== "All" && !opt.disabled) { allCmLocs.push(opt); }
      });
      setModalCmLocations(allCmLocs);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmLocationOptions]);

  // When programs API returns, apply pending selection or expand "All"
  useEffect(() => {
    if (scopeProgramsStatus !== "succeeded" || programOptions.length === 0) return;
    if (pendingModalProgramsRef.current) {
      const pending = pendingModalProgramsRef.current;
      pendingModalProgramsRef.current = null;
      if (pending.some((p: any) => p.value === "All")) {
        const allProgs: any[] = [ALL_OPTION];
        programOptions.forEach((opt) => { if (opt.value !== "All" && !opt.disabled) allProgs.push(opt); });
        setModalPrograms(allProgs);
      } else {
        setModalPrograms(pending);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeProgramsStatus, programOptions]);

  // Handle upsert API response: show flash, close modal, refresh data
  useEffect(() => {
    if (!submitting) return;
    if (upsertAssignmentStatus === "succeeded") {
      setSubmitting(false);
      showFlash("success", deleteVisible ? "Assignment(s) deleted successfully." : "Assignment saved successfully.");
      const currentLogin = meData?.user;
      let affectsCurrentUser = false;
      if (modalVisible) {
        affectsCurrentUser = modalUser.trim() === currentLogin;
        resetModalForm();
      } else if (oclModalVisible) {
        affectsCurrentUser = oclTableRows.some((r) => r.user.trim() === currentLogin);
        resetOclModal();
      } else if (deleteVisible) {
        affectsCurrentUser = viewMode === "by-user"
          ? selectedItems.some((i) => i.entity === currentLogin)
          : selectedOclItems.some((oclItem) => oclItem.users.some((u) => u.user === currentLogin));
        setDeleteVisible(false);
      } else if (statusVisible) {
        if (viewMode === "by-user") {
          affectsCurrentUser = selectedItems.some((i) => i.entity === currentLogin);
        } else {
          affectsCurrentUser = selectedOclItems.some((oclItem) => oclItem.users.some((u) => u.user === currentLogin));
        }
        setStatusVisible(false);
      }
      setSelectedItems([]);
      setSelectedOclItems([]);
      dispatch(getPermissionAssignmentsRequest({
        system: selectedSystem,
      }));
      if (affectsCurrentUser) dispatch(getPermissionMeRequest(SYSTEM));
    } else if (upsertAssignmentStatus === "failed") {
      setSubmitting(false);
      const errorMsg = upsertAssignmentError || "Failed to save assignment.";
      if (modalVisible) {
        setModalError(errorMsg);
      } else if (oclModalVisible) {
        setOclModalError(errorMsg);
      } else {
        showFlash("error", errorMsg);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertAssignmentStatus]);


  // Handle changeAssignmentStatus API response (activate/deactivate/delete via /assignments/status)
  useEffect(() => {
    if (changeAssignmentStatusState.status === "succeeded") {
      const currentLogin = meData?.user;
      if (statusVisible || deleteVisible || oclModalVisible) {
        if (statusVisible) {
          setSubmitting(false);
          setStatusVisible(false);
          showFlash("success", "Assignment status updated successfully.");
        } else if (deleteVisible) {
          setDeleteVisible(false);
          showFlash("success", "Assignment(s) deleted successfully.");
        } else {
          setSubmitting(false);
          resetOclModal();
          showFlash("success", "Assignment(s) deleted successfully.");
        }
        const affectsCurrentUser = viewMode === "by-user"
          ? selectedItems.some((i) => i.entity === currentLogin)
          : selectedOclItems.some((oclItem) => oclItem.users.some((u) => u.user === currentLogin));
        setSelectedItems([]);
        setSelectedOclItems([]);
        dispatch(getPermissionAssignmentsRequest({ system: selectedSystem }));
        if (affectsCurrentUser) dispatch(getPermissionMeRequest(SYSTEM));
      }
    } else if (changeAssignmentStatusState.status === "failed") {
      if (statusVisible) {
        setSubmitting(false);
        setStatusVisible(false);
        showFlash("error", changeAssignmentStatusState.error || "Failed to update assignment status.");
      } else if (deleteVisible) {
        setDeleteVisible(false);
        showFlash("error", changeAssignmentStatusState.error || "Failed to delete assignment.");
      } else if (oclModalVisible) {
        setSubmitting(false);
        setOclModalError(changeAssignmentStatusState.error || "Failed to delete assignment.");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeAssignmentStatusState.status]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  // Show a temporary flash notification (auto-dismiss after 3s)
  const showFlash = useCallback((type: "success" | "error", content: string) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlashItems([{ type, content, dismissible: true, onDismiss: () => setFlashItems([]) }]);
    flashTimerRef.current = setTimeout(() => setFlashItems([]), 3000);
  }, []);

  const rowClass = (status: string) => status === "inactive" ? "row-inactive" : "";

  const handleViewModeChange = (mode: DataViewMode) => {
    setViewMode(mode);
    setFilterText("");
    setSelectedItems([]);
    setSelectedOclItems([]);
  };

  // Convert selected CM/Location options to API payload format
  const parseCmLocationsToPayload = (selected: readonly any[]): { cm: string; location: string[] }[] => {
    if (selected.some((o: any) => o.value === "All") && !hasDisabledCmLocation) {
      return [{ cm: "All", location: ["All"] }];
    }
    const cmLocMap = new Map<string, string[]>();
    selected.filter((o: any) => o.value !== "All").forEach((opt: any) => {
      const [cm, loc] = (opt.value as string).split("|");
      if (!cmLocMap.has(cm)) cmLocMap.set(cm, []);
      cmLocMap.get(cm)!.push(loc);
    });
    const result: { cm: string; location: string[] }[] = [];
    cmLocMap.forEach((locs, cm) => result.push({ cm, location: locs }));
    return result;
  };

  const handleCmLocationChange = (selectedOptions: readonly any[]) => {
    handleMultiselectAll(selectedOptions, modalCmLocations, setModalCmLocations, cmLocationOptions);
    setModalPrograms([]);
    pendingModalProgramsRef.current = null;
    // Compute the resolved selection (mirrors handleMultiselectAll logic)
    const hasAll = selectedOptions.some((o) => o.value === "All");
    const prevHadAll = modalCmLocations.some((o: any) => o.value === "All");
    let resolved: readonly any[];
    if (hasAll && !prevHadAll) {
      const allLeaf: any[] = [ALL_OPTION];
      cmLocationOptions.forEach((opt) => {
        if (opt.options) { opt.options.forEach((child: any) => { if (!child.disabled) allLeaf.push(child); }); }
        else if (opt.value !== "All" && !opt.disabled) { allLeaf.push(opt); }
      });
      resolved = allLeaf;
    } else if (prevHadAll && !hasAll) {
      resolved = [];
    } else if (hasAll && selectedOptions.length > 1) {
      resolved = selectedOptions.filter((o) => o.value !== "All");
    } else {
      // Check if all enabled leaf options are now selected — auto-resolve as "All"
      const enabledLeafValues: string[] = [];
      cmLocationOptions.forEach((opt) => {
        if (opt.options) { opt.options.forEach((child: any) => { if (!child.disabled) enabledLeafValues.push(child.value); }); }
        else if (opt.value !== "All" && !opt.disabled) { enabledLeafValues.push(opt.value); }
      });
      const selectedValues = new Set(selectedOptions.map((o) => o.value));
      if (enabledLeafValues.length > 0 && enabledLeafValues.every((v) => selectedValues.has(v))) {
        resolved = [ALL_OPTION, ...selectedOptions];
      } else {
        resolved = selectedOptions;
      }
    }
    fetchScopePrograms(resolved);
  };

  const resetModalForm = () => {
    setModalSystem(selectedSystem === "All" ? null : { label: selectedSystem, value: selectedSystem });
    setModalUserType(ENTITY_TYPE_OPTIONS[0]);
    setModalUser("");
    setModalTeamDisplay("");
    setModalRoles([]);
    setModalOperations([]);
    setModalCmLocations([]);
    setModalPrograms([]);
    pendingModalProgramsRef.current = null;
    setEditingId(null);
    setEditingVersion(1);
    setModalVisible(false);
    setModalError(null);
    setEmployeeSuggestions([]);
    setTeamSuggestions([]);
  };

  const resetOclModal = () => {
    setOclEditingMode(false);
    setModalOperations([]);
    setModalCmLocations([]);
    setModalPrograms([]);
    pendingModalProgramsRef.current = null;
    setOclTableRows([]);
    setOclRemovedRows([]);
    setOclModalVisible(false);
    setOclModalError(null);
    setEmployeeSuggestions([]);
    setTeamSuggestions([]);
  };

  const handleOpenAssign = () => {
    if (viewMode === "by-ocl") {
      resetOclModal();
      // In assign mode, start with one default empty row with entityType "user"
      setOclTableRows([{ user: "", entityType: "user", roles: [] }]);
      setOclModalVisible(true);
    } else {
      resetModalForm();
      setModalVisible(true);
    }
  };

  // Populate modal fields from selected item for editing
  const handleEdit = () => {
    let rawOperations: string[] = [];
    let rawCmLocations: { cm: string; location: string[] }[] = [];
    let rawPrograms: string[] = [];

    if (viewMode === "by-user" && selectedItems.length === 1) {
      const item = selectedItems[0];
      setEditingId(item.assignment_id);
      setEditingVersion(item.version || 1);
      const matchedType = ENTITY_TYPE_OPTIONS.find((o) => o.value === item.entity_type);
      setModalUserType(matchedType || ENTITY_TYPE_OPTIONS[0]);
      setModalUser(item.entity);
      if (item.entity_type === "team") {
        setModalTeamDisplay(item.entity_display_name || item.entity);
      }
      setModalRoles(item.roles.map((r) => ({ label: r, value: r })));
      rawOperations = item.operations;
      rawCmLocations = item.cm_locations;
      rawPrograms = item.programs;
      setModalVisible(true);
    } else if (viewMode === "by-ocl" && selectedOclItems.length === 1) {
      const item = selectedOclItems[0];
      setOclEditingMode(true);
      rawOperations = [item.operation];
      const locValue = item.location;
      rawCmLocations = [{ cm: item.cm, location: [locValue] }];
      rawPrograms = [];
      oclOriginalProgramsRef.current = rawPrograms;
      oclOriginalRowsRef.current = item.users.map((u) => ({ assignmentId: u.assignmentId, user: u.user, roles: [...u.roles] }));
      setOclTableRows([
        ...item.users.map((u) => ({ assignmentId: u.assignmentId, user: u.user, entityType: u.entityType, roles: [...u.roles], version: u.version || 1, teamDisplay: u.displayName || "" })),
        { user: "", entityType: "user" as const, roles: [] },
      ]);
      setOclModalVisible(true);
    } else {
      return;
    }

    // Shared dataScope expansion logic
    if (rawOperations.includes("All")) {
      const allOps: any[] = [ALL_OPTION];
      operationOptions.forEach((opt) => { if (opt.value !== "All" && !opt.disabled) allOps.push(opt); });
      setModalOperations(allOps);
    } else {
      setModalOperations(rawOperations.map((o) => ({ label: o, value: o })));
    }

    const cmLocOpts: any[] = [];
    if (rawCmLocations.some((cl) => cl.cm === "All")) {
      cmLocOpts.push(ALL_OPTION);
      cmLocationOptions.forEach((opt) => {
        if (opt.options) { opt.options.forEach((child: any) => { if (!child.disabled) cmLocOpts.push(child); }); }
        else if (opt.value !== "All" && !opt.disabled) { cmLocOpts.push(opt); }
      });
    } else {
      rawCmLocations.forEach((cl) => {
        if (cl.location.includes("All") || cl.location.length === 0) {
          cmLocOpts.push({ label: `${cl.cm} — All Locations`, value: `${cl.cm}|All` });
        } else {
          cl.location.forEach((loc) => {
            cmLocOpts.push({ label: `${cl.cm} — ${loc}`, value: `${cl.cm}|${loc}` });
          });
        }
      });
    }
    setModalCmLocations(cmLocOpts);

    // Set pending programs and immediately show the known selection while loading
    const pendingPrograms = rawPrograms.includes("All")
      ? [ALL_OPTION]
      : rawPrograms.map((p) => ({ label: p, value: p }));
    pendingModalProgramsRef.current = pendingPrograms;
    setModalPrograms(pendingPrograms);
    fetchScopePrograms(cmLocOpts);
  };

  // Submit by-user modal: create or update assignment
  const handleModalSubmit = () => {
    const system = selectedSystem === "All" ? modalSystem?.value : selectedSystem;
    if (!system || !modalUser.trim() || modalRoles.length === 0) return;
    setModalError(null);
    setSubmitting(true);
    const cmLocations = parseCmLocationsToPayload(modalCmLocations);
    dispatch(upsertPermissionAssignmentRequest({
      ...(editingId ? { assignment_id: editingId, version: editingVersion } : {}),
      system,
      entity_type: modalUserType?.value || "user",
      entity: modalUser.trim(),
      ...(modalUserType?.value === "team" && modalTeamDisplay.trim() ? { entity_display_name: modalTeamDisplay.trim() } : {}),
      roles: modalRoles.map((r: any) => r.label),
      operations: (modalOperations.some((o: any) => o.value === "All") && !hasDisabledOperation) ? ["All"] : modalOperations.filter((o: any) => o.value !== "All").map((o: any) => o.value),
      cm_locations: cmLocations,
      programs: (modalPrograms.some((p: any) => p.value === "All") && !hasDisabledProgram) ? ["All"] : (modalPrograms.length > 0 ? modalPrograms.filter((p: any) => p.value !== "All").map((p: any) => p.value) : ["All"]),
    }));
  };

  // Submit by-ocl modal: upsert for each user (existing edits + newly added)
  const handleOclModalSubmit = () => {
    const system = selectedSystem === "All" ? modalSystem?.value : selectedSystem;
    if (!system) return;
    setSubmitting(true);
    setOclModalError(null);
    const cmLocations = parseCmLocationsToPayload(modalCmLocations);
    const operations = (modalOperations.some((o: any) => o.value === "All") && !hasDisabledOperation) ? ["All"] : modalOperations.filter((o: any) => o.value !== "All").map((o: any) => o.value);
    const programsSelected = modalPrograms.length > 0;
    const programs = (modalPrograms.some((p: any) => p.value === "All") && !hasDisabledProgram) ? ["All"] : (programsSelected ? modalPrograms.filter((p: any) => p.value !== "All").map((p: any) => p.value) : ["All"]);

    // Determine which assignmentIds were originally on this data line (from edit mode)
    const originalAssignmentIds = new Set(
      oclEditingMode && selectedOclItems.length === 1
        ? selectedOclItems[0].users.map((u) => u.assignmentId)
        : []
    );

    // Filter out empty rows (user/roles blank) and disabled assignments (not editable by current user)
    const validRows = oclTableRows.filter((row) => {
      if (!row.user.trim() || row.roles.length === 0) return false;
      if (row.assignmentId) {
        const original = assignmentsData?.assignments?.find((a) => a.assignment_id === row.assignmentId);
        if (original && isAssignmentDisabled(original.roles, original.created_by)) return false;
      }
      return true;
    });

    // Pre-compute removed rows that belong to this data line (ignore temporarily added then removed rows)
    const removedToProcess = oclRemovedRows.filter((removed) => originalAssignmentIds.has(removed.assignmentId));
    // If all rows were filtered out (e.g. disabled assignments) and nothing to remove, abort early
    if (validRows.length === 0 && removedToProcess.length === 0) {
      setSubmitting(false);
      setOclModalError("Unable to save. The selected assignment(s) are restricted from editing.");
      return;
    }

    validRows.forEach((row) => {
      if (row.assignmentId && originalAssignmentIds.has(row.assignmentId)) {
        // Existing row in edit mode: if no program selected, keep existing programs unchanged
        const original = assignmentsData?.assignments?.find(
          (a: PermissionAssignment) => a.assignment_id === row.assignmentId
        );
        const existingPrograms = original?.programs || [];
        const rowPrograms = programsSelected
          ? (existingPrograms.includes("All") || programs.includes("All")
            ? ["All"]
            : [...new Set([...existingPrograms, ...programs])])
          : existingPrograms;
        dispatch(upsertPermissionAssignmentRequest({
          assignment_id: row.assignmentId,
          system,
          entity_type: row.entityType,
          entity: row.user.trim(),
          ...(row.entityType === "team" && row.teamDisplay ? { entity_display_name: row.teamDisplay } : {}),
          roles: row.roles,
          operations,
          cm_locations: cmLocations,
          programs: rowPrograms,
          version: row.version || 1,
        }));
      } else if (row.assignmentId && !originalAssignmentIds.has(row.assignmentId)) {
        // Scene 2: user+roles exists in system but not on this line — merge current scope into existing
        const original = assignmentsData?.assignments?.find(
          (a: PermissionAssignment) => a.assignment_id === row.assignmentId
        );
        if (!original) return;
        const cmLocsIsAll = cmLocations.some((cl) => cl.cm === "All" && cl.location.includes("All"));
        const mergedCmLocs = cmLocsIsAll || original.cm_locations.some((cl) => cl.cm === "All" && cl.location.includes("All"))
          ? [{ cm: "All", location: ["All"] }]
          : (() => {
            const result = [...original.cm_locations];
            cmLocations.forEach((cur) => {
              const existing = result.find((cl) => cl.cm === cur.cm);
              if (existing) {
                if (!existing.location.includes("All")) {
                  cur.location.forEach((loc) => {
                    if (!existing.location.includes(loc)) existing.location.push(loc);
                  });
                }
              } else {
                result.push({ ...cur });
              }
            });
            return result;
          })();
        const mergedOps = operations.includes("All") || original.operations.includes("All") ? ["All"] : [...new Set([...original.operations, ...operations])];
        // Edit mode with existing assignment not on this line: if no program selected, keep existing programs
        const mergedPrograms = programsSelected
          ? (programs.includes("All") || original.programs.includes("All") ? ["All"] : [...new Set([...original.programs, ...programs])])
          : original.programs;
        dispatch(upsertPermissionAssignmentRequest({
          assignment_id: row.assignmentId,
          system,
          entity_type: row.entityType,
          entity: row.user.trim(),
          ...(row.entityType === "team" && row.teamDisplay ? { entity_display_name: row.teamDisplay } : {}),
          roles: row.roles,
          operations: mergedOps,
          cm_locations: mergedCmLocs,
          programs: mergedPrograms,
          version: original.version || 1,
        }));
      } else {
        // No assignmentId on row — check if this user+roles already exists in the system
        const existingAssignment = assignmentsData?.assignments?.find(
          (a: PermissionAssignment) =>
            a.system === system &&
            a.entity === row.user.trim() &&
            a.entity_type === row.entityType &&
            row.roles.length === a.roles.length &&
            row.roles.every((r) => a.roles.includes(r))
        );
        if (existingAssignment) {
          const cmLocsIsAll = cmLocations.some((cl) => cl.cm === "All" && cl.location.includes("All"));
          const mergedCmLocs = cmLocsIsAll || existingAssignment.cm_locations.some((cl) => cl.cm === "All" && cl.location.includes("All"))
            ? [{ cm: "All", location: ["All"] }]
            : (() => {
              const result = [...existingAssignment.cm_locations];
              cmLocations.forEach((cur) => {
                const existing = result.find((cl) => cl.cm === cur.cm);
                if (existing) {
                  if (!existing.location.includes("All")) {
                    cur.location.forEach((loc) => {
                      if (!existing.location.includes(loc)) existing.location.push(loc);
                    });
                  }
                } else {
                  result.push({ ...cur });
                }
              });
              return result;
            })();
          const mergedOps = operations.includes("All") || existingAssignment.operations.includes("All") ? ["All"] : [...new Set([...existingAssignment.operations, ...operations])];
          const mergedPrograms = programs.includes("All") || existingAssignment.programs.includes("All") ? ["All"] : [...new Set([...existingAssignment.programs, ...programs])];
          dispatch(upsertPermissionAssignmentRequest({
            assignment_id: existingAssignment.assignment_id,
            system,
            entity_type: row.entityType,
            entity: row.user.trim(),
            ...(row.entityType === "team" && row.teamDisplay ? { entity_display_name: row.teamDisplay } : {}),
            roles: row.roles,
            operations: mergedOps,
            cm_locations: mergedCmLocs,
            programs: mergedPrograms,
            version: existingAssignment.version || 1,
          }));
        } else {
          // Truly new: create new assignment, programs defaults to ["All"] if not selected
          dispatch(upsertPermissionAssignmentRequest({
            system,
            entity_type: row.entityType,
            entity: row.user.trim(),
            ...(row.entityType === "team" && row.teamDisplay ? { entity_display_name: row.teamDisplay } : {}),
            roles: row.roles,
            operations,
            cm_locations: cmLocations,
            programs,
          }));
        }
      }
    });

    removedToProcess.forEach((removed) => {
      const assignment = assignmentsData?.assignments?.find(
        (a: PermissionAssignment) => a.assignment_id === removed.assignmentId
      );
      if (!assignment) return;

      // Compute complement cm_locations: remove current line's cm/locations
      const remainingCmLocs: { cm: string; location: string[] }[] = [];
      assignment.cm_locations.forEach((cl) => {
        const matchingCm = cmLocations.find((cur) => cur.cm === cl.cm || cur.cm === "All");
        if (!matchingCm) {
          remainingCmLocs.push(cl);
        } else {
          const remainingLocs = cl.location.filter((loc) =>
            matchingCm.location.includes("All") ? false : !matchingCm.location.includes(loc)
          );
          if (remainingLocs.length > 0) {
            remainingCmLocs.push({ cm: cl.cm, location: remainingLocs });
          }
        }
      });

      if (remainingCmLocs.length === 0) {
        dispatch(changePermissionAssignmentStatusRequest({
          type: "deleted",
          ids: [removed.assignmentId],
          versions: [assignment.version || 1],
        }));
      } else {
        dispatch(upsertPermissionAssignmentRequest({
          assignment_id: removed.assignmentId,
          system,
          entity_type: removed.entityType,
          entity: removed.user,
          roles: assignment.roles,
          operations: assignment.operations,
          cm_locations: remainingCmLocs,
          programs: assignment.programs,
          version: assignment.version || 1,
        }));
      }
    });
  };

  return (
    <div className="users-roles-section" data-testid="users-roles-section">
      {flashItems.length > 0 && <Flashbar items={flashItems} />}
      <SpaceBetween size="l">
        {/* ── Role Reference (collapsible) ── */}
        <ExpandableSection data-testid="role-reference-section" headerText="Role Reference" defaultExpanded={false}>
          <div className="role-summary-bar">
            {!rolesData ? (
              <StatusIndicator type="loading">Loading roles...</StatusIndicator>
            ) : (
              [...rolesData]
                .filter((r) => selectedSystem === "All" || r.system === selectedSystem)
                .sort((a, b) => {
                  if (a.type === b.type) return 0;
                  return a.type === "system" ? -1 : 1;
                }).map((role) => (
                <div key={role.role_id} className={`role-summary-item ${role.status === "inactive" ? "row-inactive" : ""}`}>
                  <Badge color={getRoleBadgeColor(role.role_name)}>{role.role_name}</Badge>
                  <span className="role-summary-desc">{role.description}</span>
                  {role.status === "inactive" && <StatusIndicator type="stopped">Inactive</StatusIndicator>}
                </div>
              ))
            )}
          </div>
        </ExpandableSection>

        {/* ── User Assignments ── */}
        <Container
          header={
            <Header
              variant="h2"
              description={
                viewMode === "by-user"
                  ? "Assign roles and data scope to users. Each row = one user assignment."
                  : "View by Operation/CM/Location: each row shows who has access to a specific scope."
              }
              counter={`(${viewMode === "by-user" ? totalCount : filteredByOcl.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button data-testid="assignment-assign-button" iconName="add-plus" disabled={!hasPermission("permission_management.pm_assign_role")} onClick={handleOpenAssign}>Assign Role</Button>
                  <Button data-testid="assignment-edit-button" disabled={!hasSingleSelection} onClick={handleEdit}>Edit</Button>
                  <ButtonDropdown
                    data-testid="assignment-actions-dropdown"
                    items={[
                      { id: "activate", text: "Activate", disabled: viewMode === "by-ocl" || !hasSelection || (viewMode === "by-user" ? selectedItems.every((i) => i.status === "active") : selectedOclItems.every((oclItem) => oclItem.users.every((u) => u.status === "active"))) },
                      { id: "deactivate", text: "Deactivate", disabled: viewMode === "by-ocl" || !hasSelection || (viewMode === "by-user" ? selectedItems.every((i) => i.status === "inactive") : selectedOclItems.every((oclItem) => oclItem.users.every((u) => u.status === "inactive"))) },
                      { id: "delete", text: "Delete", disabled: !hasSelection },
                    ]}
                    onItemClick={({ detail }) => {
                      if (detail.id === "activate") { setStatusAction("activate"); setStatusVisible(true); }
                      else if (detail.id === "deactivate") { setStatusAction("deactivate"); setStatusVisible(true); }
                      else if (detail.id === "delete") { setDeleteVisible(true); }
                    }}
                    disabled={!hasSelection}
                  >
                    Actions
                  </ButtonDropdown>
                </SpaceBetween>
              }
            >
              User Assignments
            </Header>
          }
        >
          <SpaceBetween size="m">
            <div className="toolbar-row">
              <div className="view-mode-toggle" data-testid="view-mode-toggle">
                <span className="view-mode-label">View:</span>
                <Button
                  data-testid="view-by-user-button"
                  variant={viewMode === "by-user" ? "primary" : "normal"}
                  onClick={() => handleViewModeChange("by-user")}
                >
                  By User
                </Button>
                <Button
                  data-testid="view-by-ocl-button"
                  variant={viewMode === "by-ocl" ? "primary" : "normal"}
                  onClick={() => handleViewModeChange("by-ocl")}
                >
                  By Operation / CM / Location
                </Button>
              </div>
            </div>

            {/* ── By User Table ── */}
            {viewMode === "by-user" && (
              <Table
                data-testid="by-user-table"
                wrapLines
                loading={assignmentsStatus === "loading"}
                loadingText="Loading assignments..."
                items={sortedData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                selectionType="multi"
                isItemDisabled={(item) => isAssignmentDisabled(item.roles, item.created_by)}
                sortingColumn={sortingColumn}
                sortingDescending={sortingDescending}
                onSortingChange={({ detail }) => {
                  setSortingColumn(detail.sortingColumn);
                  setSortingDescending(detail.isDescending ?? false);
                  setCurrentPage(1);
                }}
                trackBy="assignment_id"
                resizableColumns
                columnDefinitions={[
                  {
                    id: "user",
                    header: "User",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>
                        <Box fontWeight="bold">{item.entity_display_name || item.entity}</Box>
                        <Badge color={item.entity_type === "team" ? "blue" : item.entity_type === "vendor" ? "grey" : "green"}>{ENTITY_TYPE_OPTIONS.find((o) => o.value === item.entity_type)?.label || item.entity_type}</Badge>
                      </span>
                    ),
                    sortingField: "entity",
                    width: 240,
                  },
                  ...(selectedSystem === "All" ? [{
                    id: "system",
                    header: "System",
                    cell: (item: PermissionAssignment) => (
                      <span className={rowClass(item.status)}>
                        <Badge color={item.system === "MSA" ? "blue" : item.system === "Atlas" ? "green" : "grey"}>{item.system}</Badge>
                      </span>
                    ),
                    sortingField: "system",
                    width: 110,
                  }] : []),
                  {
                    id: "dataScope",
                    header: "Data Scope",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>
                        <div className="data-scope-cell">
                          <div className="data-scope-row">
                            <span className="data-scope-label">Operation:</span>
                            {item.operations.map((o, i) => (
                              <span key={i} className={`permission-value-tag ${o === "All" ? "tag-all" : ""}`}>{o}</span>
                            ))}
                          </div>
                          <div className="data-scope-row">
                            <span className="data-scope-label">CM:</span>
                            {item.cm_locations.map((cl, i) => (
                              <span key={i} className={`permission-value-tag ${cl.cm === "All" ? "tag-all" : ""}`}>{cl.cm}</span>
                            ))}
                          </div>
                          <div className="data-scope-row">
                            <span className="data-scope-label">Location:</span>
                            {([] as string[]).concat(...item.cm_locations.map((cl) => cl.location)).map((l, i) => (
                              <span key={i} className={`permission-value-tag ${l === "All" ? "tag-all" : ""}`}>{l}</span>
                            ))}
                          </div>
                          <div className="data-scope-row">
                            <span className="data-scope-label">Program:</span>
                            {item.programs.map((p, i) => (
                              <span key={i} className={`permission-value-tag ${p === "All" ? "tag-all" : ""}`}>{p}</span>
                            ))}
                          </div>
                        </div>
                      </span>
                    ),
                    // minWidth: 320,
                    width: 320
                  },
                  {
                    id: "role",
                    header: "Role",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>
                        <div className="badge-wrap">
                          {item.roles.map((r, i) => (
                            <Badge key={i} color={getRoleBadgeColor(r)}>{r}</Badge>
                          ))}
                        </div>
                      </span>
                    ),
                    width: 200,
                  },
                  {
                    id: "created_by",
                    header: "Creator",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>{item.created_by}</span>
                    ),
                    sortingField: "created_by",
                    width: 220,
                  },
                  {
                    id: "assigned_by",
                    header: "Updater",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        {item.assigned_by}
                      </span>
                    ),
                    sortingField: "assigned_by",
                    width: 220,
                  },
                  {
                    id: "updated_at",
                    header: "Updated DateTime",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>{formatDateTime(item.updated_at)}</span>
                    ),
                    sortingField: "updated_at",
                    width: 180,
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: (item) => (
                      <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                        {item.status === "active" ? "Active" : "Inactive"}
                      </StatusIndicator>
                    ),
                    width: 100,
                  },
                ]}
                filter={
                  <TextFilter
                    data-testid="by-user-filter"
                    filteringText={filterText}
                    filteringPlaceholder="Search by user, role, operation, or CM..."
                    onChange={({ detail }) => { setFilterText(detail.filteringText); setCurrentPage(1); }}
                  />
                }
                pagination={
                  <Pagination
                    data-testid="by-user-pagination"
                    currentPageIndex={currentPage}
                    pagesCount={Math.ceil(sortedData.length / PAGE_SIZE)}
                    onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                  />
                }
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No user assignments</b>
                    <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                      No assignments match the current filters.
                    </Box>
                  </Box>
                }
              />
            )}

            {/* ── By Operation/CM/Location Table ── */}
            {viewMode === "by-ocl" && (
              <SpaceBetween size="s">
              {(() => {
                const legendItems = (rolesData ? (selectedSystem === "All" ? rolesData : rolesData.filter((r) => r.system === selectedSystem)) : [])
                  .filter((r, i, arr) => arr.findIndex((x) => x.role_name === r.role_name) === i)
                  .sort((a, b) => {
                    if (a.type === "system" && b.type !== "system") return -1;
                    if (a.type !== "system" && b.type === "system") return 1;
                    return 0;
                  });
                const maxVisible = 5;
                const hasMore = legendItems.length > maxVisible;
                const visibleItems = legendExpanded ? legendItems : legendItems.slice(0, maxVisible);
                return (
                  <div className="role-legend">
                    {visibleItems.map((r) => (
                      <span key={r.role_id} className="role-legend-item">
                        <Badge color={getRoleBadgeColor(r.role_name)}>{getRoleAbbreviation(r.role_name)}</Badge>
                        <span className="role-legend-label">= {r.role_name}</span>
                      </span>
                    ))}
                    {hasMore && (
                      <Button variant="inline-link" onClick={() => setLegendExpanded(!legendExpanded)}>
                        {legendExpanded ? "Show less" : `+${legendItems.length - maxVisible} more`}
                      </Button>
                    )}
                  </div>
                );
              })()}
              <Table
                data-testid="by-ocl-table"
                wrapLines
                loading={assignmentsStatus === "loading"}
                loadingText="Loading assignments..."
                items={filteredByOcl.slice((oclCurrentPage - 1) * PAGE_SIZE, oclCurrentPage * PAGE_SIZE)}
                selectedItems={selectedOclItems}
                onSelectionChange={({ detail }) => setSelectedOclItems(detail.selectedItems)}
                selectionType="multi"
                isItemDisabled={(item) => {
                  const system = selectedSystem === "All" ? SYSTEM : selectedSystem;
                  const myRoles = new Set<string>();
                  meData?.assignments?.filter((a) => a.system === system).forEach((a) => a.roles.forEach((r) => myRoles.add(r)));
                  const isCustomOnly = !meData?.is_system_admin?.[system] && !myRoles.has("Administrator");
                  if (isCustomOnly) return true;
                  return isDataScopeDisabled({ operation: item.operation, cm: item.cm, location: item.location });
                }}
                trackBy="id"
                columnDefinitions={[
                  ...(selectedSystem === "All" ? [{
                    id: "system",
                    header: "System",
                    cell: (item: OclViewItem) => (
                      <span className={rowClass(item.status)}>
                        <Badge color={item.system === "MSA" ? "blue" : item.system === "Atlas" ? "green" : "grey"}>{item.system}</Badge>
                      </span>
                    ),
                    sortingField: "system",
                    width: 110,
                  }] : []),
                  {
                    id: "operation",
                    header: "Operation",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>
                        <Box fontWeight="bold">{item.operation}</Box>
                      </span>
                    ),
                    sortingField: "operation",
                    width: 120,
                  },
                  {
                    id: "cm",
                    header: "CM",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>
                        <Box fontWeight="bold">{item.cm}</Box>
                      </span>
                    ),
                    sortingField: "cm",
                    width: 120,
                  },
                  {
                    id: "location",
                    header: "Location",
                    cell: (item) => (
                      <span className={rowClass(item.status)}>
                        <span className={`permission-value-tag ${item.location === "All" ? "tag-all" : ""}`}>
                          {item.location === "All" ? "All Locations" : item.location}
                        </span>
                      </span>
                    ),
                    width: 140,
                  },
                  {
                    id: "users",
                    header: "Authorized Users",
                    cell: (item) => (
                      <div className="cm-view-users">
                        {item.users.map((u, i) => (
                          <span key={i} className={`cm-view-user-chip ${u.status === "inactive" ? "row-inactive" : ""}`}>
                            <span className="cm-view-user-name">{u.displayName || u.user}</span>
                            {u.roles.map((r, ri) => (
                              <RoleBadgeWithTooltip key={ri} role={r} color={getRoleBadgeColor(r)} />
                            ))}
                            {u.programs[0] !== "All" && (
                              <span className="cm-view-user-programs">
                                ({u.programs.join(", ")})
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    ),
                    width: 250,
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: (item) => (
                      <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                        {item.status === "active" ? "Active" : "Inactive"}
                      </StatusIndicator>
                    ),
                    width: 100,
                  },
                ]}
                filter={
                  <TextFilter
                    data-testid="by-ocl-filter"
                    filteringText={filterText}
                    filteringPlaceholder="Search by operation, CM, location, or user..."
                    onChange={({ detail }) => { setFilterText(detail.filteringText); setOclCurrentPage(1); }}
                  />
                }
                pagination={
                  <Pagination
                    data-testid="by-ocl-pagination"
                    currentPageIndex={oclCurrentPage}
                    pagesCount={Math.ceil(filteredByOcl.length / PAGE_SIZE)}
                    onChange={({ detail }) => setOclCurrentPage(detail.currentPageIndex)}
                  />
                }
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No data found</b>
                    <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                      No results match the current filters.
                    </Box>
                  </Box>
                }
              />
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>

      {/* ── By User Assign/Edit Modal ── */}
      <Modal
        data-testid="assignment-modal"
        visible={modalVisible}
        onDismiss={resetModalForm}
        header={editingId ? "Edit Assignment" : "Assign Role"}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="assignment-modal-cancel" variant="link" onClick={resetModalForm}>Cancel</Button>
              <Button
                data-testid="assignment-modal-submit"
                variant="primary"
                loading={submitting}
                disabled={!modalUser.trim() || modalRoles.length === 0 || modalOperations.length === 0 || modalCmLocations.length === 0 || (modalUserType?.value === "user" && !isValidEmail(modalUser.trim())) || (modalUserType?.value === "vendor" && !isValidVendorEmail(modalUser.trim())) || isDuplicateAssignment}
                onClick={handleModalSubmit}
              >
                {editingId ? "Save" : "Assign"}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {modalError && (
            <Alert type="error" dismissible onDismiss={() => setModalError(null)}>
              {modalError.includes('Version conflict')
                ? <>This data has been modified by another user. <strong>Please refresh and try again.</strong></>
                : modalError}
            </Alert>
          )}
          {isDuplicateAssignment && (
            <Alert type="warning">
              {editingId
                ? "Another assignment with the same user and role(s) already exists. Please choose different role(s) to avoid duplication."
                : "This user already has the selected role(s) assigned. Duplicate role assignment is not allowed. To modify data scope, please use the Edit function."}
            </Alert>
          )}
          {selectedSystem === "All" && (
            <FormField
              label={<span>System <span className="required-asterisk">*</span></span>}
              description="Select the system this assignment applies to."
            >
              <Select
                data-testid="assignment-system-select"
                selectedOption={modalSystem}
                onChange={({ detail }) => setModalSystem(detail.selectedOption)}
                options={systemOptions}
                placeholder="Select system"
              />
            </FormField>
          )}

          <FormField
            label={<span>User / Team / Vendor <span className="required-asterisk">*</span></span>}
            description="Select a type and enter a user login, team name, or vendor identifier."
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ width: "180px", flexShrink: 0 }}>
                <Select
                  data-testid="assignment-user-type-select"
                  selectedOption={modalUserType}
                  onChange={({ detail }) => {
                    setModalUserType(detail.selectedOption);
                    setModalUser("");
                    setModalTeamDisplay("");
                    setEmployeeSuggestions([]);
                    setTeamSuggestions([]);
                  }}
                  options={ENTITY_TYPE_OPTIONS}
                  disabled={!!editingId}
                />
              </div>
              <div style={{ flex: 1, position: "relative" }}>
                {modalUserType?.value === "user" ? (
                  <Autosuggest
                    data-testid="assignment-user-autosuggest"
                    value={modalUser}
                    onChange={({ detail }) => {
                      const cleanValue = detail.value.replace(/[^a-zA-Z0-9@._\-+]/g, "").slice(0, 100);
                      setModalUser(cleanValue);
                      fetchEmployeeSuggestions(cleanValue);
                    }}
                    onSelect={({ detail }) => {
                      const selectedValue = detail.value.replace(/[^a-zA-Z0-9@._\-+]/g, "").slice(0, 100);
                      setModalUser(selectedValue);
                      setEmployeeSuggestions([]);
                    }}
                    options={employeeSuggestions}
                    filteringType="manual"
                    placeholder="e.g. john@amazon.com"
                    enteredTextLabel={(value) => `Use: "${value}"`}
                    statusType={employeeSearchLoading ? "loading" : "finished"}
                    loadingText="Searching..."
                    empty="No employees found"
                    disabled={!!editingId}
                    invalid={!!modalUser.trim() && !isValidEmail(modalUser.trim())}
                  />
                ) : modalUserType?.value === "team" ? (
                  <>
                    <Autosuggest
                      data-testid="assignment-team-autosuggest"
                      value={modalTeamDisplay}
                      onChange={({ detail }) => {
                        const cleanValue = detail.value.replace(/[^a-zA-Z0-9@._\-+\s]/g, "").slice(0, 100);
                        setModalTeamDisplay(cleanValue);
                        setModalUser("");
                        fetchTeamSuggestions(cleanValue);
                      }}
                      onSelect={({ detail }) => {
                        if (teamSearchTimerRef.current) clearTimeout(teamSearchTimerRef.current);
                        const selected = teamSuggestions.find((t) => t.value === detail.value);
                        if (!selected) return;
                        setModalTeamDisplay(selected.label);
                        setModalUser(selected.value);
                        setTeamSuggestions([]);
                      }}
                      options={teamSuggestions}
                      filteringType="manual"
                      placeholder="Search and select a team"
                      statusType={teamSearchLoading ? "loading" : "finished"}
                      loadingText="Searching..."
                      empty="No teams found"
                      disabled={!!editingId}
                      invalid={!!modalTeamDisplay.trim() && !modalUser.trim()}
                    />
                    {modalTeamDisplay.trim() && !modalUser.trim() && (
                      <span className="field-error-msg">Please select a team from the search results.</span>
                    )}
                  </>
                ) : (
                  <Input
                    data-testid="assignment-user-input"
                    value={modalUser}
                    onChange={({ detail }) => {
                      const sanitized = detail.value.replace(/[^a-zA-Z0-9@._\-+]/g, "").slice(0, 100);
                      setModalUser(sanitized);
                    }}
                    placeholder="e.g. vendorname@lassostg.labcollab.net"
                    disabled={!!editingId}
                    invalid={modalUserType?.value === "vendor" && !!modalUser.trim() && !isValidVendorEmail(modalUser.trim())}
                  />
                )}
                {modalUserType?.value === "user" && modalUser.trim() && !isValidEmail(modalUser.trim()) && (
                  <span className="field-error-msg">Please enter an @amazon.com or @lab126.com email address.</span>
                )}
                {modalUserType?.value === "vendor" && modalUser.trim() && !isValidVendorEmail(modalUser.trim()) && (
                  <span className="field-error-msg">Please enter an @lassostg.labcollab.net email address.</span>
                )}
              </div>
            </div>
          </FormField>

          <FormField
            label={<span>Role <span className="required-asterisk">*</span></span>}
            description={`Determines feature access. You can assign roles below your level (current: ${currentUserRoles.join(", ") || "Unknown"}).`}
          >
            <Multiselect
              data-testid="assignment-role-multiselect"
              selectedOptions={modalRoles}
              onChange={({ detail }) => setModalRoles(detail.selectedOptions)}
              options={assignableRoleOptions}
              placeholder="Select role(s)"
              tokenLimit={3}
            />
          </FormField>

          {modalRolePreviews.length > 0 && (
            <div className="role-preview-box">
              <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: "xxs" }}>Selected roles can access:</Box>
              <div className="permission-tags-wrap">
                {modalRolePreviews.map((r) => {
                  const isAllAccess = r.permissions.length === 1 && r.permissions[0].feature === "All" && r.permissions[0].actions.includes("All");
                  return (
                    <div key={r.role_id} style={{ marginBottom: 4 }}>
                      <Badge color={getRoleBadgeColor(r.role_name)}>{r.role_name}</Badge>{" "}
                      {isAllAccess ? (
                        <span className="permission-tag">All Features: All Actions</span>
                      ) : (
                        r.permissions
                          .filter((p) => p.actions.length > 0)
                          .map((p) => (
                            <span key={p.feature} className="permission-tag permission-tag-bg">
                              <span className="permission-tag-feature-bold">{getFeatureLabel(p.feature)}</span>
                              {": "}{p.actions.map((a) => actionIdToName.get(a) || a).join(", ")}
                            </span>
                          ))
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Box variant="h4" margin={{ top: "xs" }}>Data Scope</Box>

          <FormField label={<span>Operation <span className="required-asterisk">*</span></span>}>
            <Multiselect
              data-testid="assignment-operation-multiselect"
              selectedOptions={modalOperations}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, modalOperations, setModalOperations, operationOptions)}
              options={operationOptions}
              placeholder="Select operation(s)"
              tokenLimit={3}
            />
          </FormField>

          <FormField label={<span>CM / Location <span className="required-asterisk">*</span></span>}>
            <Multiselect
              data-testid="assignment-cm-location-multiselect"
              selectedOptions={modalCmLocations.map((o: any) => {
                if (o.value === "All" || !o.value?.includes("|")) return o;
                const [cm, loc] = o.value.split("|");
                return { ...o, label: `${cm} / ${loc}` };
              })}
              onChange={({ detail }) => handleCmLocationChange(detail.selectedOptions)}
              options={cmLocationOptions}
              placeholder="Select CM / Location(s)"
              tokenLimit={3}
              filteringType="auto"
            />
          </FormField>

          <FormField label="Program">
            <Multiselect
              data-testid="assignment-program-multiselect"
              selectedOptions={modalPrograms}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, modalPrograms, setModalPrograms, programOptions)}
              options={modalCmLocations.length > 0 ? programOptions : []}
              placeholder={modalCmLocations.length > 0 ? "Select program(s) — defaults to All" : "Please select CM / Location first"}
              tokenLimit={3}
              statusType={programsLoading ? "loading" : "finished"}
              loadingText="Loading programs..."
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* ── By OCL Assign/Edit Modal ── */}
      <Modal
        data-testid="ocl-modal"
        visible={oclModalVisible}
        onDismiss={resetOclModal}
        header={oclEditingMode ? `Edit — ${selectedOclItems[0]?.operation ?? ""} / ${selectedOclItems[0]?.cm ?? ""} / ${selectedOclItems[0]?.location === "All" ? "All Locations" : selectedOclItems[0]?.location ?? ""}` : "Assign Role"}
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="ocl-modal-cancel" variant="link" onClick={resetOclModal}>Cancel</Button>
              <Button
                data-testid="ocl-modal-submit"
                variant="primary"
                loading={submitting}
                disabled={
                  modalOperations.length === 0 ||
                  modalCmLocations.length === 0 ||
                  !oclHasChanges ||
                  // Exclude empty rows (both user and roles blank), then require at least one valid row
                  // and every non-empty row must have both user and roles filled
                  (() => {
                    const validRows = oclTableRows.filter((row) => row.user.trim() || row.roles.length > 0);
                    return validRows.length === 0 || validRows.some((row) => !row.user.trim() || row.roles.length === 0);
                  })()
                }
                onClick={handleOclModalSubmit}
              >
                {oclEditingMode ? "Save" : "Assign"}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {oclModalError && (
            <Alert type="error" dismissible onDismiss={() => setOclModalError(null)}>
              {oclModalError.includes('Version conflict')
                ? <>This data has been modified by another user. <strong>Please refresh and try again.</strong></>
                : oclModalError}
            </Alert>
          )}
          {!oclEditingMode && selectedSystem === "All" && (
            <div style={{ width: "calc(50% - 8px)" }}>
              <FormField
                label={<span>System <span className="required-asterisk">*</span></span>}
                description="Select the system this assignment applies to."
              >
                <Select
                  selectedOption={modalSystem}
                  onChange={({ detail }) => setModalSystem(detail.selectedOption)}
                  options={systemOptions}
                  placeholder="Select system"
                />
              </FormField>
            </div>
          )}

          <Box variant="h4">Data Scope</Box>

          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1 }}>
              <FormField label={<span>Operation <span className="required-asterisk">*</span></span>}>
                <Multiselect
                  data-testid="ocl-operation-multiselect"
                  selectedOptions={modalOperations}
                  onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, modalOperations, setModalOperations, operationOptions)}
                  options={operationOptions}
                  placeholder="Select operation(s)"
                  tokenLimit={3}
                  disabled={oclEditingMode}
                />
              </FormField>
            </div>
            <div style={{ flex: 1 }}>
              <FormField label={<span>CM / Location <span className="required-asterisk">*</span></span>}>
                <Multiselect
                  data-testid="ocl-cm-location-multiselect"
                  selectedOptions={modalCmLocations.map((o: any) => {
                    if (o.value === "All" || !o.value?.includes("|")) return o;
                    const [cm, loc] = o.value.split("|");
                    return { ...o, label: `${cm} / ${loc}` };
                  })}
                  onChange={({ detail }) => handleCmLocationChange(detail.selectedOptions)}
                  options={cmLocationOptions}
                  placeholder="Select CM / Location(s)"
                  tokenLimit={3}
                  filteringType="auto"
                  disabled={oclEditingMode}
                />
              </FormField>
            </div>
          </div>

          <div style={{ width: "calc(50% - 8px)" }}>
            <FormField label="Program">
              <Multiselect
                data-testid="ocl-program-multiselect"
                selectedOptions={modalPrograms}
                onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, modalPrograms, setModalPrograms, programOptions)}
                options={modalCmLocations.length > 0 ? programOptions : []}
                placeholder={modalCmLocations.length > 0 ? "Select program(s) — defaults to All" : "Please select CM / Location first"}
                tokenLimit={3}
                statusType={programsLoading ? "loading" : "finished"}
                loadingText="Loading programs..."
              />
            </FormField>
          </div>

          {/* Users table */}
          <Box variant="h4">Users on this line <span className="required-asterisk">*</span></Box>
          {!oclEditingMode && oclTableRows.some((row) => isRowExistingInSystem(row)) && (
            <Alert type="info" data-testid="existing-user-warning">
              Rows marked with info icon indicate users/roles that already exist in the system. Assigning will update their Data Scope.
            </Alert>
          )}
            {(() => {
            const oclScopeIncomplete = !oclEditingMode && (modalOperations.length === 0 || modalCmLocations.length === 0);
            return (
            <div className="ocl-users-table">
            <Table
              data-testid="ocl-users-table"
              items={oclUsersTableItems}
              trackBy="idx"
              columnDefinitions={[
                {
                  id: "user",
                  header: <span>User / Team / Vendor <span className="required-asterisk">*</span></span>,
                  cell: (item) => {
                    const userDisabled = !!item.assignmentId || oclScopeIncomplete;
                    const isAmazonUser = item.entityType === "user";
                    const updateField = (patch: Partial<{ assignmentId?: string; user: string; entityType: EntityType; roles: string[]; teamDisplay?: string }>) => {
                      const updated = [...oclTableRows];
                      updated[item.idx] = { ...updated[item.idx], ...patch };
                      setOclTableRows(updated);
                    };
                    const isTeam = item.entityType === "team";
                    const handleUserChange = (value: string) => {
                      const sanitized = value.replace(/[^a-zA-Z0-9@._\-+\s]/g, "").slice(0, 100);
                      if (isTeam) {
                        updateField({ user: "", teamDisplay: sanitized, assignmentId: undefined, roles: [] });
                        fetchTeamSuggestions(sanitized);
                      } else {
                        updateField({ user: sanitized, assignmentId: undefined, roles: [] });
                        if (isAmazonUser) fetchEmployeeSuggestions(sanitized);
                      }
                    };
                    const handleSelectExisting = (selectedValue: string) => {
                      const assignment = assignmentsData?.assignments?.find(
                        (a: PermissionAssignment) => a.assignment_id === selectedValue
                      );
                      if (assignment) {
                        const updated = [...oclTableRows];
                        updated[item.idx] = {
                          ...updated[item.idx],
                          assignmentId: assignment.assignment_id,
                          user: assignment.entity,
                          entityType: assignment.entity_type,
                          roles: [...assignment.roles],
                        };
                        // Auto-append empty row for next input
                        if (item.idx === updated.length - 1) {
                          updated.push({ user: "", entityType: "user", roles: [] });
                        }
                        setOclTableRows(updated);
                      } else {
                        updateField({ user: selectedValue.replace(/[^a-zA-Z0-9@._\-+]/g, "").slice(0, 100) });
                      }
                      setEmployeeSuggestions([]);
                    };
                    // Merge existing assignment suggestions with employee search results
                    const filteredExisting = existingAssignmentSuggestions.filter(
                      (s) => !item.user.trim() || s.label.toLowerCase().includes(item.user.trim().toLowerCase())
                    );
                    const mergedOptions = [
                      ...(filteredExisting.length > 0 ? [{ label: "Existing Users & Roles", options: filteredExisting }] : []),
                      ...(employeeSuggestions.length > 0 ? [{ label: "Employee Search", options: employeeSuggestions }] : []),
                    ];
                    return (
                      <div>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <div style={{ width: "136px", flexShrink: 0 }}>
                            <Select
                              selectedOption={{ label: ENTITY_TYPE_LABEL[item.entityType], value: item.entityType }}
                              onChange={({ detail }) => {
                                const et = (detail.selectedOption?.value || "user") as EntityType;
                                updateField({ user: "", entityType: et, roles: [], assignmentId: undefined, teamDisplay: "" });
                                setEmployeeSuggestions([]);
                                setTeamSuggestions([]);
                              }}
                              options={ENTITY_TYPE_OPTIONS}
                              expandToViewport
                              disabled={userDisabled}
                            />
                          </div>
                          <div style={{ flex: 1, position: "relative" }}>
                            {isAmazonUser ? (
                              <Autosuggest
                                value={item.user}
                                onChange={({ detail }) => handleUserChange(detail.value)}
                                onSelect={({ detail }) => handleSelectExisting(detail.value)}
                                options={mergedOptions as any}
                                filteringType="manual"
                                expandToViewport
                                placeholder="e.g. john@amazon.com"
                                enteredTextLabel={(value) => `Use: "${value}"`}
                                statusType={employeeSearchLoading ? "loading" : "finished"}
                                loadingText="Searching..."
                                empty="Type to search or select existing"
                                disabled={userDisabled}
                                invalid={!userDisabled && !!item.user.trim() && !isValidEmail(item.user.trim())}
                              />
                            ) : isTeam ? (
                              <Autosuggest
                                value={item.teamDisplay || ""}
                                onChange={({ detail }) => handleUserChange(detail.value)}
                                onSelect={({ detail }) => {
                                  if (teamSearchTimerRef.current) clearTimeout(teamSearchTimerRef.current);
                                  const selected = teamSuggestions.find((t) => t.value === detail.value);
                                  if (!selected) return;
                                  updateField({ user: selected.value, teamDisplay: selected.label });
                                  setTeamSuggestions([]);
                                }}
                                options={teamSuggestions}
                                filteringType="manual"
                                expandToViewport
                                placeholder="Search and select a team"
                                statusType={teamSearchLoading ? "loading" : "finished"}
                                loadingText="Searching..."
                                empty="No teams found"
                                disabled={userDisabled}
                                invalid={!userDisabled && !!(item.teamDisplay || "").trim() && !item.user.trim()}
                              />
                            ) : (
                              <Input
                                value={item.user}
                                onChange={({ detail }) => handleUserChange(detail.value)}
                                placeholder="e.g. vendorname@lassostg.labcollab.net"
                                disabled={userDisabled}
                                invalid={item.entityType === "vendor" && !userDisabled && !!item.user.trim() && !isValidVendorEmail(item.user.trim())}
                              />
                            )}
                            {!userDisabled && isAmazonUser && item.user.trim() && !isValidEmail(item.user.trim()) && (
                              <span className="field-error-msg">Please enter an @amazon.com or @lab126.com email address.</span>
                            )}
                            {!userDisabled && isTeam && (item.teamDisplay || "").trim() && !item.user.trim() && (
                              <span className="field-error-msg">Please select a team from the search results.</span>
                            )}
                            {!userDisabled && item.entityType === "vendor" && item.user.trim() && !isValidVendorEmail(item.user.trim()) && (
                              <span className="field-error-msg">Please enter an @lassostg.labcollab.net email address.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  },
                  width: "50%",
                },
                {
                  id: "role",
                  header: <span>Role <span className="required-asterisk">*</span></span>,
                  cell: (item) => {
                    const selectedRoles = item.roles.map((r: string) => ({ label: r, value: r }));
                    const rolesLocked = !!item.assignmentId || oclScopeIncomplete;
                    // Disable roles already assigned to this user in other assignments
                    const existingRoles = new Set<string>();
                    if (item.user.trim() && assignmentsData?.assignments) {
                      assignmentsData.assignments.forEach((a) => {
                        if (a.entity === item.user.trim() && a.assignment_id !== item.assignmentId) {
                          a.roles.forEach((r) => existingRoles.add(r));
                        }
                      });
                    }
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ flex: 1 }}>
                          <Multiselect
                            selectedOptions={selectedRoles}
                            onChange={({ detail }) => {
                              const newRoles = detail.selectedOptions.map((o: any) => o.value);
                              const updated = [...oclTableRows];
                              updated[item.idx] = { ...updated[item.idx], roles: newRoles };
                              setOclTableRows(updated);
                            }}
                            options={assignableRoleOptions}
                            placeholder="Select role(s)"
                            tokenLimit={2}
                            expandToViewport
                            disabled={rolesLocked}
                          />
                        </div>
                        {!oclEditingMode && isRowExistingInSystem(item) && (
                          <span title="Existing in system">
                            <Icon name="status-info" variant="link" />
                          </span>
                        )}
                      </div>
                    );
                  },
                  width: "30%",
                },
                {
                  id: "actions",
                  header: "Actions",
                  cell: (item) => {
                    // Last row (the input row) shows "Add" button
                    if (item.idx === oclTableRows.length - 1 && !item.assignmentId) {
                      const currentRowEmpty = !item.user.trim() || item.roles.length === 0;
                      const isEmailInvalid = item.entityType === "user" && !!item.user.trim() && !isValidEmail(item.user.trim());
                      const isVendorInvalid = item.entityType === "vendor" && !!item.user.trim() && !isValidVendorEmail(item.user.trim());
                      return (
                        <Button
                          variant="normal"
                          disabled={oclScopeIncomplete || currentRowEmpty || isEmailInvalid || isVendorInvalid}
                          onClick={() => {
                            setOclTableRows([...oclTableRows, { user: "", entityType: "user", roles: [] }]);
                          }}
                        >Add</Button>
                      );
                    }
                    const originalAssignment = item.assignmentId
                      ? assignmentsData?.assignments?.find((a) => a.assignment_id === item.assignmentId)
                      : undefined;
                    const rowDisabled = originalAssignment
                      ? isAssignmentDisabled(originalAssignment.roles, originalAssignment.created_by)
                      : false;
                    const isDisabled = oclScopeIncomplete || rowDisabled;
                    if (isDisabled) return null;
                    return (
                      <span
                        className="ocl-remove-btn"
                        onClick={() => {
                          const row = oclTableRows[item.idx];
                          if (row.assignmentId) {
                            setOclRemovedRows((prev) => [...prev, { assignmentId: row.assignmentId!, user: row.user, entityType: row.entityType }]);
                          }
                          setOclTableRows(oclTableRows.filter((_, i) => i !== item.idx));
                        }}
                      >
                        <Icon name="remove" />
                      </span>
                    );
                  },
                  width: "16%",
                },
              ]}
              empty={<Box textAlign="center" color="inherit"><b>No users assigned</b></Box>}
            />
            </div>
            );
            })()}
        </SpaceBetween>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal
        data-testid="assignment-delete-modal"
        visible={deleteVisible}
        onDismiss={() => setDeleteVisible(false)}
        header="Confirm Delete"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="assignment-delete-cancel" variant="link" onClick={() => setDeleteVisible(false)}>Cancel</Button>
              <Button data-testid="assignment-delete-confirm" variant="primary" onClick={() => {
                // By-user view: directly delete the selected assignments
                if (viewMode === "by-user" && selectedItems.length > 0) {
                  dispatch(changePermissionAssignmentStatusRequest({
                    type: "deleted",
                    ids: selectedItems.map((item) => item.assignment_id),
                    versions: selectedItems.map((item) => item.version || 1),
                  }));
                } else if (viewMode === "by-ocl" && selectedOclItems.length > 0) {
                  setSubmitting(true);
                  const deleteIds: string[] = [];
                  const deleteVersions: number[] = [];
                  selectedOclItems.forEach((oclItem) => {
                    const lineCmLocs: { cm: string; location: string[] }[] = oclItem.cm === "All"
                      ? [{ cm: "All", location: ["All"] }]
                      : [{ cm: oclItem.cm, location: [oclItem.location] }];

                    oclItem.users.forEach((u) => {
                      const assignment = assignmentsData?.assignments?.find(
                        (a: PermissionAssignment) => a.assignment_id === u.assignmentId
                      );
                      if (!assignment) return;

                      // Compute remaining cm_locations after removing this line's scope
                      const remainingCmLocs: { cm: string; location: string[] }[] = [];
                      assignment.cm_locations.forEach((cl) => {
                        const matchingCm = lineCmLocs.find((cur) => cur.cm === cl.cm || cur.cm === "All");
                        if (!matchingCm) {
                          remainingCmLocs.push(cl);
                        } else {
                          const remainingLocs = cl.location.filter((loc) =>
                            matchingCm.location.includes("All") ? false : !matchingCm.location.includes(loc)
                          );
                          if (remainingLocs.length > 0) {
                            remainingCmLocs.push({ cm: cl.cm, location: remainingLocs });
                          }
                        }
                      });

                      if (remainingCmLocs.length === 0) {
                        deleteIds.push(u.assignmentId);
                        deleteVersions.push(u.version || 1);
                      } else {
                        dispatch(upsertPermissionAssignmentRequest({
                          assignment_id: u.assignmentId,
                          system: assignment.system,
                          entity_type: u.entityType,
                          entity: u.user,
                          roles: assignment.roles,
                          operations: assignment.operations,
                          cm_locations: remainingCmLocs,
                          programs: assignment.programs,
                          version: assignment.version || 1,
                        }));
                      }
                    });
                  });
                  if (deleteIds.length > 0) {
                    dispatch(changePermissionAssignmentStatusRequest({
                      type: "deleted",
                      ids: deleteIds,
                      versions: deleteVersions,
                    }));
                  }
                }
              }}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        {viewMode === "by-user" ? (
          <SpaceBetween size="s">
            <Box variant="p">
              Are you sure you want to delete {selectedItems.length} user(roles)?
            </Box>
            <Alert type="warning">
              The following user(roles) will be deleted:
              <Box margin={{ top: "xs" }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {selectedItems.map((item) => (
                    <li key={item.assignment_id}>{item.entity} ({item.roles.join(", ")})</li>
                  ))}
                </ul>
              </Box>
            </Alert>
          </SpaceBetween>
        ) : (
          <SpaceBetween size="s">
            <Box variant="p">
              Are you sure you want to remove the following users (roles) from the selected data lines?
            </Box>
            <Alert type="warning">
              The following users (roles) will be removed from the selected data lines:
              {selectedOclItems.map((oclItem) => (
                <Box key={oclItem.id} margin={{ top: "xs" }}>
                  <strong>{oclItem.operation} / {oclItem.cm} / {oclItem.location === "All" ? "All Locations" : oclItem.location}</strong>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {oclItem.users.map((u) => (
                      <li key={u.assignmentId}>{u.user} ({u.roles.join(", ")})</li>
                    ))}
                  </ul>
                </Box>
              ))}
            </Alert>
          </SpaceBetween>
        )}
      </Modal>

      {/* ── Status Change Modal ── */}
      <Modal
        data-testid="assignment-status-modal"
        visible={statusVisible}
        onDismiss={() => setStatusVisible(false)}
        header={`Confirm ${statusAction === "activate" ? "Activate" : "Deactivate"}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="assignment-status-cancel" variant="link" onClick={() => setStatusVisible(false)}>Cancel</Button>
              <Button data-testid="assignment-status-confirm" variant="primary" loading={submitting} disabled={(() => {
                const target = statusAction === "activate" ? "active" : "inactive";
                if (viewMode === "by-user") return selectedItems.every((i) => i.status === target);
                const usersToUpdate = new Map<string, boolean>();
                selectedOclItems.forEach((oclItem) => {
                  oclItem.users.forEach((u) => { if (!usersToUpdate.has(u.assignmentId)) usersToUpdate.set(u.assignmentId, u.status !== target); });
                });
                return Array.from(usersToUpdate.values()).every((needsUpdate) => !needsUpdate);
              })()} onClick={() => {
                const targetStatus = statusAction === "activate" ? "active" : "inactive";
                setSubmitting(true);
                let ids: string[] = [];
                let versions: number[] = [];
                if (viewMode === "by-user") {
                  const items = selectedItems.filter((item) => item.status !== targetStatus);
                  ids = items.map((item) => item.assignment_id);
                  versions = items.map((item) => item.version || 1);
                } else {
                  const dispatched = new Set<string>();
                  selectedOclItems.forEach((oclItem) => {
                    oclItem.users.forEach((u) => {
                      if (u.status === targetStatus || dispatched.has(u.assignmentId)) return;
                      dispatched.add(u.assignmentId);
                      ids.push(u.assignmentId);
                      versions.push(u.version || 1);
                    });
                  });
                }
                if (ids.length > 0) {
                  dispatch(changePermissionAssignmentStatusRequest({
                    type: targetStatus,
                    ids,
                    versions,
                  }));
                }
              }}>Confirm</Button>
            </SpaceBetween>
          </Box>
        }
      >
        {(() => {
          const targetStatus = statusAction === "activate" ? "active" : "inactive";
          if (viewMode === "by-user") {
            const toUpdateCount = selectedItems.filter((i) => i.status !== targetStatus).length;
            const skippedCount = selectedItems.length - toUpdateCount;
            return (
              <SpaceBetween size="s">
                <Box variant="p">
                  Are you sure you want to {statusAction}{" "}
                  {toUpdateCount === 1
                    ? <>the user(roles) <strong>{selectedItems.find((i) => i.status !== targetStatus)?.entity}</strong></>
                    : <><strong>{toUpdateCount}</strong> user(roles)</>
                  }?
                </Box>
                {skippedCount > 0 && (
                  <Box variant="p" color="text-body-secondary">
                    Note: {skippedCount} user(roles) already {targetStatus} will be skipped.
                  </Box>
                )}
                <Alert type="warning">
                  The following user(roles) will be {statusAction === "activate" ? "activated" : "deactivated"}:
                  <Box margin={{ top: "xs" }}>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {selectedItems.filter((i) => i.status !== targetStatus).map((item) => (
                        <li key={item.assignment_id}>{item.entity} ({item.roles.join(", ")})</li>
                      ))}
                    </ul>
                  </Box>
                </Alert>
              </SpaceBetween>
            );
          }
          // OCL view: count by unique user (assignmentId)
          const allUsers = new Map<string, { user: string; roles: string[]; status: string }>();
          selectedOclItems.forEach((oclItem) => {
            oclItem.users.forEach((u) => {
              if (!allUsers.has(u.assignmentId)) {
                allUsers.set(u.assignmentId, { user: u.user, roles: u.roles, status: u.status });
              }
            });
          });
          const usersToUpdate = Array.from(allUsers.values()).filter((u) => u.status !== targetStatus);
          const skippedCount = allUsers.size - usersToUpdate.length;
          return (
            <SpaceBetween size="s">
              <Box variant="p">
                Are you sure you want to {statusAction}{" "}
                {usersToUpdate.length === 1
                  ? <><strong>{usersToUpdate[0].user}</strong></>
                  : <><strong>{usersToUpdate.length}</strong> assignment(s)</>
                }?
              </Box>
              {skippedCount > 0 && (
                <Box variant="p" color="text-body-secondary">
                  Note: {skippedCount} user(roles) already {targetStatus} will be skipped.
                </Box>
              )}
              <Alert type="warning">
                The following users (roles) will be {statusAction === "activate" ? "activated" : "deactivated"}:
                <Box margin={{ top: "xs" }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {usersToUpdate.map((u) => (
                      <li key={u.user}>{u.user} ({u.roles.join(", ")})</li>
                    ))}
                  </ul>
                </Box>
              </Alert>
            </SpaceBetween>
          );
        })()}
      </Modal>
    </div>
  );
};

export default UsersAndRolesTab;
